import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { onAuthStateChanged, getAuth, User } from "firebase/auth";
import {
  doc,
  getDoc,
  getFirestore,
  collection,
  collectionGroup,
  query,
  where,
  getDocs,
  setDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { app } from "../config/firebaseConfig";
import * as Sentry from "@sentry/browser";
import { BusinessData } from "@/types/businessData";
import { BusinessMember } from "@/types/businessMember";

interface ProfileContextProps {
  businessProfile: BusinessData | null;
  memberProfile: BusinessMember | null;
  loading: boolean;
  inactiveUser: boolean;
  setProfileCreated?: (created: boolean) => void;
  unreadMessages: number;
  setUnreadMessages: (unreadMessages: number) => void;
  signInAsBusinessMember: (businessUid: string) => Promise<void>;
  signOutAsBusinessMember: () => Promise<void>;
  isSignedInAsBusinessMember: boolean;
  emailPreferences?: {
    notifyOnMatches: boolean;
  };
  toggleNotifyOnMatches: (enabled: boolean) => Promise<void>;
}

const ProfileContext = createContext<ProfileContextProps | undefined>(
  undefined
);

interface ProfileProviderProps {
  children: ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({
  children,
}) => {
  const [businessProfile, setBusinessProfile] = useState<BusinessData | null>(
    null
  );
  const [memberProfile, setMemberProfile] = useState<BusinessMember | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [inactiveUser, setInactiveUser] = useState<boolean>(false);
  const [profileCreated, setProfileCreated] = useState<boolean>(false);
  const [unreadMessages, setUnreadMessages] = useState<number>(0);
  const [isSignedInAsBusinessMember, setIsSignedInAsBusinessMember] =
    useState<boolean>(false);

  const auth = getAuth(app);
  const db = getFirestore(app);

  // Function to sign in as a business member
  const signInAsBusinessMember = async (businessUid: string) => {
    if (!businessProfile?.isAdmin) {
      return;
    }
    try {
      // Fetch Business document using the provided business UID
      const businessRef = doc(db, "Businesses", businessUid);
      const businessSnap = await getDoc(businessRef);

      if (businessSnap.exists()) {
        const businessData = businessSnap.data() as BusinessData;
        setBusinessProfile(businessData);
        setIsSignedInAsBusinessMember(true);
      } else {
        console.log("No matching business document found");
      }
    } catch (error) {
      console.error("Error signing in as business member: ", error);
    }
  };

  const signOutAsBusinessMember = async () => {
    const user = auth.currentUser;
    if (!user) {
      console.error("Cant sign out as business member, no user found");
      return;
    }
    fetchBusinessMemberAndProfile(user);
    setIsSignedInAsBusinessMember(false);
  };

  const fetchBusinessMemberAndProfile = useCallback(async (user: User) => {
    try {
      // Fetch BusinessMember document (user account data)
      const memberQuery = query(
        collection(db, "BusinessMember"),
        where("uid", "==", user.uid)
      );
      const memberSnapshot = await getDocs(memberQuery);

      if (!memberSnapshot.empty) {
        const memberDoc = memberSnapshot.docs[0];
        const memberData = memberDoc.data() as BusinessMember;
        setMemberProfile(memberData);
        console.log(
          "✅ Found existing BusinessMember document for user:",
          memberData.name
        );

        Sentry.setUser({
          id: memberData.uid,
          email: memberData.email,
        });

        // Query all businesses where this user is a member through subcollections
        console.log(
          "🔍 Searching for business memberships for user:",
          user.uid
        );

        let businessesSnapshot;
        try {
          const businessesQuery = query(
            collectionGroup(db, "members"),
            where("uid", "==", user.uid)
          );
          businessesSnapshot = await getDocs(businessesQuery);
          console.log(
            "📊 Business memberships found:",
            businessesSnapshot.docs.length
          );
        } catch (error) {
          console.error("❌ CollectionGroup query failed:", error);
          console.log("🔄 Falling back to alternative approach...");

          // Fallback: Query all businesses and check their members subcollections
          const allBusinessesQuery = query(collection(db, "Businesses"));
          const allBusinessesSnapshot = await getDocs(allBusinessesQuery);

          const userMemberships = [];
          for (const businessDoc of allBusinessesSnapshot.docs) {
            const membersQuery = query(
              collection(db, "Businesses", businessDoc.id, "members"),
              where("uid", "==", user.uid)
            );
            const membersSnapshot = await getDocs(membersQuery);
            if (!membersSnapshot.empty) {
              userMemberships.push({
                businessId: businessDoc.id,
                memberData: membersSnapshot.docs[0].data(),
              });
            }
          }

          // Create a mock snapshot-like object
          businessesSnapshot = {
            empty: userMemberships.length === 0,
            docs: userMemberships.map((membership) => ({
              ref: {
                parent: {
                  parent: { id: membership.businessId },
                },
              },
              data: () => membership.memberData,
            })),
          };

          console.log(
            "📊 Business memberships found (fallback):",
            businessesSnapshot.docs.length
          );
        }

        if (!businessesSnapshot.empty) {
          // User is a member of one or more businesses
          const businessMemberships = businessesSnapshot.docs.map((doc) => ({
            businessId: doc.ref.parent.parent?.id,
            memberData: doc.data(),
          }));

          console.log("🏢 Business memberships:", businessMemberships);

          // For now, take the first business membership as primary
          // TODO: Implement logic to handle multiple business memberships
          const primaryMembership = businessMemberships[0];
          if (primaryMembership.businessId) {
            console.log(
              "🎯 Loading business data for:",
              primaryMembership.businessId
            );
            const businessRef = doc(
              db,
              "Businesses",
              primaryMembership.businessId
            );
            const businessSnap = await getDoc(businessRef);

            if (businessSnap.exists()) {
              const businessData = businessSnap.data() as BusinessData;
              setBusinessProfile(businessData);
              console.log(
                "✅ Found business membership for user:",
                businessData.name
              );
            } else {
              console.log(
                "❌ Business document not found for ID:",
                primaryMembership.businessId
              );
            }
          } else {
            console.log("❌ No business ID found in membership data");
          }
        } else {
          console.log(
            "🔍 No matching BusinessMember document found, checking if user is a business owner..."
          );

          const businessRef = doc(db, "Businesses", user.uid);
          const businessSnap = await getDoc(businessRef);

          if (businessSnap.exists()) {
            // User is an existing business owner - create their personal account document
            const businessData = businessSnap.data() as BusinessData;
            console.log(
              "🔄 Creating personal account for existing business owner:",
              businessData
            );
            const now = serverTimestamp();

            // Create clean user account document without business references
            const newMemberData: BusinessMember = {
              uid: user.uid,
              name: businessData.name || user.displayName || "",
              email: businessData.email || user.email || "",
              role: "admin", // Business owners are admins of their own business
              invitedAt: now as any,
              createdAt: now as any,
              updatedAt: now as any,
            };

            const newMemberRef = doc(db, "BusinessMember", user.uid);
            console.log(
              "🔄 Creating NEW BusinessMember document for legacy business owner"
            );
            await setDoc(newMemberRef, newMemberData);

            setMemberProfile(newMemberData);
            setBusinessProfile(businessData);

            console.log(
              "✅ Created personal account document for existing business owner"
            );
          } else {
            // Create a new standalone user account
            const now = serverTimestamp();
            const newMemberData: BusinessMember = {
              uid: user.uid,
              name: user.displayName || "",
              email: user.email || "",
              role: "member",
              invitedAt: now as any,
              createdAt: now as any,
              updatedAt: now as any,
            };

            const newMemberRef = doc(db, "BusinessMember", user.uid);
            console.log("🔄 Creating NEW standalone BusinessMember document");
            await setDoc(newMemberRef, newMemberData);
            setMemberProfile(newMemberData);
            setBusinessProfile(null); // No business association for new standalone users
            console.log("✅ Created new standalone user account");
          }
        }
      }
    } catch (error) {
      console.error("❌ Error fetching data: ", error);
    } finally {
      setLoading(false);
      console.log("🏁 Profile fetch completed");
    }
  }, [db]);

  const toggleNotifyOnMatches = async (enabled: boolean) => {
    if (!businessProfile) return;
    try {
      const businessRef = doc(db, "Businesses", businessProfile.uid);
      const newEmailPreferences = {
        ...(businessProfile.emailPreferences || {}),
        notifyOnMatches: enabled,
      };
      await updateDoc(businessRef, {
        emailPreferences: newEmailPreferences,
      });
      setBusinessProfile({
        ...businessProfile,
        emailPreferences: newEmailPreferences,
      });
    } catch (error) {
      console.error("Error updating matches notification setting: ", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchBusinessMemberAndProfile(user);
      } else {
        setLoading(false);
        setMemberProfile(null);
        setBusinessProfile(null);
        Sentry.setUser(null);
        // OneSignal.logout();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [auth, fetchBusinessMemberAndProfile, profileCreated]);

  useEffect(() => {
    if (!businessProfile) return;

    const subscriptionStatus = businessProfile.subscription?.status;
    const hasActiveSubscription =
      subscriptionStatus === "active" || subscriptionStatus === "trialing";

    if (businessProfile.subscription) {
      setInactiveUser(!hasActiveSubscription);
      return;
    }

    const legacyActive =
      businessProfile.sub_still_active === "active" || businessProfile.trial;
    setInactiveUser(!legacyActive);
  }, [businessProfile]);

  return (
    <ProfileContext.Provider
      value={{
        businessProfile,
        memberProfile,
        loading,
        inactiveUser,
        setProfileCreated,
        unreadMessages,
        setUnreadMessages,
        signInAsBusinessMember,
        signOutAsBusinessMember,
        isSignedInAsBusinessMember,
        emailPreferences: businessProfile?.emailPreferences || {
          notifyOnMatches: false,
        },
        toggleNotifyOnMatches,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error("useUsers must be used within a UsersProvider");
  }
  return context;
};
